import { useMemo } from "react"
import { formatDateString } from "@/v2-common/utils/utils.date"
import { formatNumber } from "@/v2-common/utils/utils.formatting"
import cn from "@/v2-ui/utils/utils.cn"
import Table, {
  createColumns,
  useTableQuery,
  type TableProps,
} from "@/v2-backoffice-client/components/table"
import RelativeDate from "@/v2-backoffice-client/components/RelativeDate"
import { useAgentConversationQuery } from "@/v2-backoffice-client/agentConversation/agentConversation.query"
import type { AgentConversationFragment } from "@/v2-backoffice-client/agentConversation/__types__/agentConversation.fragment"
import {
  RUNTIME_LABELS,
  SURFACE_LABELS,
  TENANT_NAME_COLUMN,
  USER_EMAIL_COLUMN,
} from "@/v2-backoffice-client/agentConversation/agentConversation.constants"
import { useAppViewSheetOpener } from "@/v2-backoffice-client/app/app.utils"

const PAGE_SIZE = 100

const COLUMN_SIZING = {
  UserEmail: 160,
  TenantName: 160,
  Surface: 100,
  Runtime: 110,
  TurnCount: 70,
  ToolCallCount: 90,
  InputTokens: 90,
  OutputTokens: 90,
  LastTurnAt: 130,
}

function createAgentConversationColumns() {
  return createColumns<AgentConversationFragment>({
    enableSorting: true,
    enableHiding: true,
    columns: {
      LastTurnAt: {
        id: "LastTurnAt",
        label: "Last Activity",
        filter: { type: "date-range" },
        renderCell({ row }) {
          return <RelativeDate date={row.original.LastTurnAt} />
        },
        exportValue({ row }) {
          return formatDateString(row.original.LastTurnAt)
        },
      },
      // Id is UserEmail (not UserName) because the filter/sort id doubles as
      // the server column name, and the registry only has UserEmail — see
      // agentConversation.api.go's agentConversationsFilterRegistry.
      [USER_EMAIL_COLUMN]: {
        id: USER_EMAIL_COLUMN,
        label: "User",
        filter: { type: "text", placeholder: "Search by email..." },
        to: ({ row }) => `?v=user:${row.original.UserId}`,
        renderCell({ row }) {
          return row.original.UserName || row.original.UserEmail || "—"
        },
        exportValue({ row }) {
          return row.original.UserEmail ?? String(row.original.UserId)
        },
      },
      [TENANT_NAME_COLUMN]: {
        id: TENANT_NAME_COLUMN,
        label: "Team",
        filter: { type: "text", placeholder: "Filter by team..." },
        to: ({ row }) => {
          if (!row.original.TenantId) return
          return `?v=tenant:${row.original.TenantId}`
        },
        renderCell({ row }) {
          return row.original.TenantName || "—"
        },
      },
      Surface: {
        id: "Surface",
        label: "Chat",
        filter: {
          type: "select",
          options: Object.entries(SURFACE_LABELS).map(([value, label]) => ({
            value,
            label,
          })),
        },
        renderCell({ row }) {
          return SURFACE_LABELS[row.original.Surface] ?? row.original.Surface
        },
      },
      Runtime: {
        id: "Runtime",
        label: "Runtime",
        filter: {
          type: "select",
          options: Object.entries(RUNTIME_LABELS).map(([value, label]) => ({
            value,
            label,
          })),
        },
        renderCell({ row }) {
          return RUNTIME_LABELS[row.original.Runtime] ?? row.original.Runtime
        },
      },
      TurnCount: {
        id: "TurnCount",
        label: "Turns",
        filter: { type: "number-range" },
      },
      ToolCallCount: {
        id: "ToolCallCount",
        label: "Tool Calls",
        enableSorting: false,
      },
      InputTokens: {
        id: "InputTokens",
        label: "Tokens In",
        enableSorting: false,
        renderCell({ row }) {
          if (row.original.InputTokens == null) return null
          return formatNumber(Number(row.original.InputTokens))
        },
      },
      OutputTokens: {
        id: "OutputTokens",
        label: "Tokens Out",
        enableSorting: false,
        renderCell({ row }) {
          if (row.original.OutputTokens == null) return null
          return formatNumber(Number(row.original.OutputTokens))
        },
      },
      FirstPrompt: {
        id: "FirstPrompt",
        label: "First Message",
        enableSorting: false,
        to: ({ row }) => `?v=agentConversation:${row.original.Id}`,
        renderCell({ row }) {
          return (
            <span title={row.original.FirstPrompt ?? undefined}>
              {row.original.FirstPrompt || "—"}
            </span>
          )
        },
      },
      Id: { id: "Id", label: "Id", defaultHidden: true },
    },
  })
}

export type AgentConversationTableProps = Omit<
  TableProps<AgentConversationFragment>,
  "data" | "columns"
>

function AgentConversationTable(props: AgentConversationTableProps) {
  const { className, ...tableProps } = props

  const opener = useAppViewSheetOpener()
  const columns = useMemo(() => createAgentConversationColumns(), [])

  const {
    items,
    pageInfo,
    loading,
    error,
    refetch,
    onFetchMore,
    onQueryChange,
  } = useTableQuery({
    pageSize: PAGE_SIZE,
    useQuery: useAgentConversationQuery,
    select(data) {
      return data.getAgentConversations
    },
  })

  return (
    <Table
      className={cn("agent-conversation-table", className)}
      tableId="agent-conversation-table"
      data={items}
      columns={columns}
      initialColumnSizing={COLUMN_SIZING}
      manualSorting
      manualFiltering
      onQueryChange={onQueryChange}
      getHighlightedRowId={(row) => {
        return opener.getIsActive("agentConversation", row.original.Id)
      }}
      {...tableProps}
    >
      <Table.Toolbar>
        <Table.Search
          column={TENANT_NAME_COLUMN}
          placeholder="Search by team..."
        />
        <div className="flex-1" />
        <Table.ToolbarFilters />
        <Table.ToolbarMenu
          onRefresh={refetch}
          exportFileName="agent-conversations"
        />
      </Table.Toolbar>

      <Table.FilterList />

      <Table.Content
        countLabel="conversations"
        isLoading={loading}
        error={error}
        pageInfo={pageInfo}
        onFetchMore={onFetchMore}
      />
    </Table>
  )
}

export default AgentConversationTable
